<template>
  <div class="auth-view" :class="{ 'is-register': tab === 'register' }">
    <aside class="scene-panel" aria-label="温暖的聊天场景">
      <div class="scene-glow" aria-hidden="true"></div>

      <a class="brand-word" href="#/home" aria-label="xyo.chat 首页">xyo.chat</a>

      <div class="lamp-wrap" aria-hidden="true">
        <div class="lamp">
          <span class="lamp-knob"></span>
          <span class="lamp-shade"></span>
          <span class="lamp-stem"></span>
          <span class="lamp-cord"></span>
          <span class="lamp-pull"></span>
          <span class="lamp-base"></span>
        </div>
      </div>

      <transition-group
        name="bubble-change"
        tag="div"
        class="conversation"
        aria-label="聊天片段"
        appear
      >
        <article
          v-for="(bubble, index) in activeBubbles"
          :key="tab + bubble.text"
          class="chat-bubble"
          :class="{ outgoing: index === 1 }"
        >
          <p>{{ bubble.text }}</p>
          <span class="bubble-time">
            {{ index === 0 ? "20:18" : index === 1 ? "20:19" : "20:20" }}
            <i v-if="index === 1" aria-hidden="true">✓✓</i>
          </span>
        </article>
      </transition-group>

      <div class="typing-bubble" aria-label="对方正在输入">
        <i></i><i></i><i></i>
      </div>

      <div class="table" aria-hidden="true">
        <span class="table-surface"></span>
        <span class="table-front"></span>
        <span class="table-leg"></span>
      </div>

      <svg class="steam steam-left" viewBox="0 0 76 92" aria-hidden="true">
        <path d="M20 86 C8 69, 34 59, 21 40 C13 28, 20 17, 27 8" />
        <path d="M48 88 C35 70, 62 60, 48 42 C39 30, 49 18, 53 9" />
      </svg>
      <svg class="steam steam-right" viewBox="0 0 76 102" aria-hidden="true">
        <path d="M20 96 C5 76, 37 65, 22 43 C12 29, 19 16, 29 7" />
        <path d="M50 95 C35 75, 66 62, 51 41 C41 27, 50 15, 55 6" />
      </svg>

      <img
        class="still-life"
        src="@/assert/login-register-v2/still-life-v2.webp"
        alt=""
        aria-hidden="true"
      />
    </aside>

    <main class="form-panel">
      <div class="form-shell">
        <transition name="heading-change" mode="out-in">
          <header :key="tab" class="panel-copy">
            <h1>{{ panelTitle }}</h1>
            <p class="sr-only">{{ panelSubtitle }}</p>
          </header>
        </transition>

        <nav class="tabs" role="tablist" aria-label="账号操作">
          <button
            id="login-tab"
            type="button"
            role="tab"
            :aria-selected="tab === 'login'"
            :tabindex="tab === 'login' ? 0 : -1"
            :class="{ on: tab === 'login' }"
            @click="tab = 'login'"
          >
            登录
          </button>
          <button
            id="register-tab"
            type="button"
            role="tab"
            :aria-selected="tab === 'register'"
            :tabindex="tab === 'register' ? 0 : -1"
            :class="{ on: tab === 'register' }"
            @click="tab = 'register'"
          >
            注册
          </button>
        </nav>

        <transition name="form-change" mode="out-in">
          <section
            :key="tab"
            class="form-stage"
            role="tabpanel"
            :aria-labelledby="tab === 'login' ? 'login-tab' : 'register-tab'"
          >
            <el-form
              v-if="tab === 'login'"
              label-position="top"
              class="auth-form login-form"
              @submit.native.prevent="doLogin"
            >
              <el-form-item class="field-item">
                <label class="field-label" for="login-email">邮箱</label>
                <div class="field-shell">
                  <el-input
                    id="login-email"
                    v-model="loginForm.email"
                    placeholder="请输入邮箱"
                    autocomplete="username"
                  />
                </div>
              </el-form-item>

              <el-form-item class="field-item">
                <label class="field-label" for="login-password">密码</label>
                <div class="field-shell has-toggle">
                  <el-input
                    id="login-password"
                    v-model="loginForm.password"
                    :type="showLoginPassword ? 'text' : 'password'"
                    placeholder="请输入密码"
                    autocomplete="current-password"
                  />
                  <button
                    type="button"
                    class="field-toggle"
                    :aria-pressed="showLoginPassword"
                    aria-label="切换密码可见性"
                    @click="showLoginPassword = !showLoginPassword"
                  >
                    {{ showLoginPassword ? "隐藏" : "显示" }}
                  </button>
                </div>
              </el-form-item>

              <div class="minor-row">
                <button
                  type="button"
                  class="check-control"
                  :aria-pressed="remember"
                  @click="remember = !remember"
                >
                  <span class="check-box" :class="{ checked: remember }" aria-hidden="true"></span>
                  <span>记住我</span>
                </button>
                <a href="#">忘记密码？</a>
              </div>

              <el-button type="primary" native-type="submit" class="submit-btn" :loading="loading">
                立即登录
              </el-button>

              <p class="legal-copy">
                登录即表示你同意
                <a href="#">《用户协议》</a>
                与
                <a href="#">《隐私政策》</a>
              </p>
            </el-form>

            <el-form
              v-else
              label-position="top"
              class="auth-form register-form"
              @submit.native.prevent="doRegister"
            >
              <el-form-item class="field-item">
                <label class="field-label" for="register-name">昵称（选填）</label>
                <div class="field-shell">
                  <el-input
                    id="register-name"
                    v-model="regForm.display_name"
                    placeholder="怎么称呼你"
                    autocomplete="nickname"
                  />
                </div>
              </el-form-item>

              <el-form-item class="field-item">
                <label class="field-label" for="register-email">邮箱</label>
                <div class="field-shell">
                  <el-input
                    id="register-email"
                    v-model="regForm.email"
                    placeholder="请输入邮箱"
                    autocomplete="email"
                  />
                </div>
              </el-form-item>

              <div class="register-profile-row">
                <el-form-item class="field-item">
                  <label class="field-label" for="register-age">年龄（须满 18 周岁）</label>
                  <div class="field-shell">
                    <el-input
                      id="register-age"
                      v-model.number="regForm.age"
                      type="number"
                      :min="18"
                      :max="120"
                      placeholder="请输入年龄"
                      autocomplete="bday-year"
                    />
                  </div>
                </el-form-item>

                <el-form-item class="field-item">
                  <label class="field-label" for="register-gender">性别</label>
                  <div class="field-shell">
                    <el-select
                      id="register-gender"
                      v-model="regForm.gender"
                      placeholder="请选择性别"
                      style="width: 100%"
                    >
                      <el-option label="女" value="女" />
                      <el-option label="男" value="男" />
                      <el-option label="其他" value="其他" />
                    </el-select>
                  </div>
                </el-form-item>
              </div>

              <el-form-item class="field-item">
                <label class="field-label" for="register-invite">邀请码（选填）</label>
                <div class="field-shell">
                  <el-input
                    id="register-invite"
                    v-model="regForm.invite_code"
                    placeholder="请输入邀请码"
                  />
                </div>
              </el-form-item>

              <el-form-item class="field-item">
                <label class="field-label" for="register-code">验证码</label>
                <div class="code-row">
                  <div class="field-shell code-field">
                    <el-input
                      id="register-code"
                      v-model="regForm.code"
                      placeholder="6 位验证码"
                      autocomplete="one-time-code"
                    />
                  </div>
                  <el-button
                    class="code-btn"
                    native-type="button"
                    :disabled="regCodeLeft > 0"
                    @click="sendCode"
                  >
                    {{ regCodeText }}
                  </el-button>
                </div>
              </el-form-item>

              <el-form-item class="field-item register-password">
                <label class="field-label" for="register-password">密码</label>
                <div class="field-shell has-toggle">
                  <el-input
                    id="register-password"
                    v-model="regForm.password"
                    :type="showRegisterPassword ? 'text' : 'password'"
                    placeholder="请输入密码"
                    autocomplete="new-password"
                  />
                  <button
                    type="button"
                    class="field-toggle"
                    :aria-pressed="showRegisterPassword"
                    aria-label="切换密码可见性"
                    @click="showRegisterPassword = !showRegisterPassword"
                  >
                    {{ showRegisterPassword ? "隐藏" : "显示" }}
                  </button>
                </div>
              </el-form-item>

              <div
                class="check-control agreement"
                role="button"
                tabindex="0"
                :aria-pressed="agreed"
                @click="agreed = !agreed"
                @keydown.enter.prevent="agreed = !agreed"
                @keydown.space.prevent="agreed = !agreed"
              >
                <span class="check-box" :class="{ checked: agreed }" aria-hidden="true"></span>
                <span>
                  我已阅读并同意
                  <a href="#" @click.prevent.stop>《用户协议》</a>
                  与
                  <a href="#" @click.prevent.stop>《隐私政策》</a>
                </span>
              </div>

              <el-button type="primary" native-type="submit" class="submit-btn" :loading="loading">
                立即注册
              </el-button>

              <div class="panel-footer">
                <span>已有账号？</span>
                <button type="button" class="link-button" @click="tab = 'login'">去登录</button>
              </div>
            </el-form>
          </section>
        </transition>

        <p class="icp-copy">
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
            苏ICP备15020291号-2
          </a>
        </p>
      </div>
    </main>
  </div>
</template>

<script>
import { authApi, getToken, setAuth } from "@/services/api";

const CODE_COOLDOWN_SECONDS = 120;

export default {
  name: "LoginRegisterView",
  data() {
    return {
      tab: "login",
      loading: false,
      remember: true,
      agreed: true,
      showLoginPassword: false,
      showRegisterPassword: false,
      loginForm: { email: "", password: "" },
      regForm: { display_name: "", email: "", age: null, gender: "", invite_code: "", code: "", password: "" },
      regCodeLeft: 0,
      regCodeTimer: null,
      loginBubbles: [
        { text: "到家了吗？" },
        { text: "嗯，刚到。" },
        { text: "那先歇一会儿。" },
      ],
      registerBubbles: [
        { text: "第一次见面。" },
        { text: "嗯，请多关照。" },
        { text: "以后慢慢认识。" },
      ],
      features: [
        { title: "长期记忆", desc: "记住你的点滴与偏好" },
        { title: "角色卡", desc: "自定义性格与背景" },
        { title: "主动陪伴", desc: "懂你所需，主动关心" },
        { title: "隐私安全", desc: "端到端保护，安心倾诉" },
      ],
    };
  },
  computed: {
    regCodeText() {
      return this.regCodeLeft > 0 ? `${this.regCodeLeft}s` : "发送验证码";
    },
    panelTitle() {
      return this.tab === "login" ? "回来啦" : "认识一下";
    },
    panelSubtitle() {
      return this.tab === "login"
        ? "回到你们上次停下的地方。"
        : "用一个账号，保存之后的每次相遇。";
    },
    activeBubbles() {
      return this.tab === "login" ? this.loginBubbles : this.registerBubbles;
    },
  },
  created() {
    this.applyInviteFromRoute();
    if (getToken()) this.$router.replace("/chat");
  },
  watch: {
    "$route.query": {
      handler() {
        this.applyInviteFromRoute();
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.stopRegCodeCountdown();
  },
  methods: {
    async doLogin() {
      if (!this.loginForm.email || !this.loginForm.password) {
        this.$message.warning("请输入邮箱和密码");
        return;
      }
      this.loading = true;
      try {
        const res = await authApi.login(this.loginForm.email, this.loginForm.password);
        setAuth(res.access_token, res.user);
        this.$message.success(`欢迎回来，${res.user.display_name}`);
        this.$router.push("/chat");
      } catch (e) {
        this.$message.error(e.message || "登录失败");
      } finally {
        this.loading = false;
      }
    },
    async sendCode() {
      if (!this.regForm.email) {
        this.$message.warning("请先填写邮箱");
        return;
      }
      try {
        await authApi.sendCode(this.regForm.email);
        this.startRegCodeCountdown();
        this.$message.success("验证码已发送");
      } catch (e) {
        this.$message.error(e.message || "发送失败");
      }
    },
    startRegCodeCountdown() {
      this.stopRegCodeCountdown();
      this.regCodeLeft = CODE_COOLDOWN_SECONDS;
      this.regCodeTimer = window.setInterval(() => {
        this.regCodeLeft -= 1;
        if (this.regCodeLeft <= 0) this.stopRegCodeCountdown();
      }, 1000);
    },
    stopRegCodeCountdown() {
      if (this.regCodeTimer) {
        window.clearInterval(this.regCodeTimer);
        this.regCodeTimer = null;
      }
      if (this.regCodeLeft < 0) this.regCodeLeft = 0;
    },
    async doRegister() {
      const age = Number(this.regForm.age);
      if (!this.regForm.email || !this.regForm.code || !this.regForm.password) {
        this.$message.warning("请填写邮箱、验证码和密码");
        return;
      }
      if (!Number.isInteger(age) || age < 18 || age > 120) {
        this.$message.warning("未满 18 周岁不能注册");
        return;
      }
      if (!this.regForm.gender) {
        this.$message.warning("请选择性别");
        return;
      }
      if (!this.agreed) {
        this.$message.warning("请先阅读并同意用户协议与隐私政策");
        return;
      }
      this.loading = true;
      try {
        const res = await authApi.register({
          ...this.regForm,
          age,
          display_name: this.regForm.display_name.trim(),
          invite_code: this.regForm.invite_code.trim(),
        });
        setAuth(res.access_token, res.user);
        this.$message.success("注册成功");
        this.$router.push("/chat");
      } catch (e) {
        this.$message.error(e.message || "注册失败");
      } finally {
        this.loading = false;
      }
    },
    applyInviteFromRoute() {
      const q = this.$route.query || {};
      const code = q.invite || q.invite_code || q.code || q.ref;
      if (code) {
        this.regForm.invite_code = String(code).trim();
        this.tab = "register";
      }
    },
  },
};
</script>

<style scoped lang="scss">
.auth-view {
  --cream: #fef3e4;
  --cream: color(display-p3 0.996 0.953 0.894);
  --cream-soft: #fefaf3;
  --cream-soft: color(display-p3 0.996 0.98 0.953);
  --cream-deep: #f7dec1;
  --orange: #fd6328;
  --orange: color(display-p3 0.992 0.388 0.157);
  --orange-deep: #eb4f19;
  --orange-deep: color(display-p3 0.922 0.31 0.098);
  --orange-soft: #feddbd;
  --orange-soft: color(display-p3 0.996 0.867 0.741);
  --cocoa: #433125;
  --cocoa: color(display-p3 0.263 0.192 0.145);
  --body: #594c3f;
  --body: color(display-p3 0.349 0.298 0.247);
  --muted: #8d8071;
  --muted: color(display-p3 0.553 0.502 0.443);
  --line: #eadcc8;
  --line: color(display-p3 0.918 0.863 0.784);

  position: relative;
  display: grid;
  grid-template-columns: 60.287% 39.713%;
  width: 100%;
  min-width: 960px;
  min-height: 100vh;
  min-height: 100svh;
  overflow: hidden;
  background: var(--cream);
  color: var(--cocoa);
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  isolation: isolate;
}

button,
a,
input {
  -webkit-tap-highlight-color: transparent;
}

button:focus-visible,
a:focus-visible {
  outline: 3px solid rgba(253, 99, 40, 0.28);
  outline-offset: 3px;
}

.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.scene-panel {
  position: relative;
  min-width: 0;
  min-height: 941px;
  height: 100vh;
  height: 100svh;
  overflow: hidden;
}

.scene-panel::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 12;
  pointer-events: none;
  background: linear-gradient(90deg, transparent 88%, rgba(254, 243, 228, 0.5) 97%, var(--cream) 100%);
}

.scene-glow {
  position: absolute;
  top: 286px;
  left: -70px;
  z-index: 0;
  width: 610px;
  height: 430px;
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(255, 248, 190, 0.34) 0%, rgba(255, 226, 177, 0.11) 58%, transparent 80%);
  filter: blur(22px);
  animation: glow-breathe 5.8s ease-in-out infinite;
}

.brand-word {
  position: absolute;
  top: 54px;
  left: 59px;
  z-index: 20;
  color: var(--orange);
  font-family: Arial, Helvetica, sans-serif;
  font-size: 31px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.065em;
  text-decoration: none;
  transform-origin: left center;
  animation: brand-reveal 0.62s 0.08s cubic-bezier(0.2, 0.78, 0.22, 1) both;
}

.lamp-wrap {
  position: absolute;
  top: 250px;
  left: 54px;
  z-index: 4;
  width: 300px;
  height: 501px;
  transform: scale(0.79);
  transform-origin: top left;
  isolation: isolate;
}

.lamp-wrap::before {
  content: "";
  position: absolute;
  top: 15px;
  left: -120px;
  z-index: 0;
  width: 540px;
  height: 520px;
  border-radius: 50%;
  pointer-events: none;
  background: radial-gradient(
    ellipse 58% 84% at 50% 18%,
    rgba(255, 237, 187, 0.2) 0%,
    rgba(255, 220, 151, 0.105) 34%,
    rgba(255, 199, 105, 0.035) 58%,
    transparent 78%
  );
  filter: blur(32px);
  opacity: 0.72;
  animation: ambient-light-breathe 6.8s ease-in-out infinite;
}

.lamp-wrap::after {
  content: none;
}

.lamp {
  position: relative;
  z-index: 1;
  width: 300px;
  height: 501px;
  filter: drop-shadow(0 16px 18px rgba(197, 89, 29, 0.12));
  animation: lamp-settle 0.82s 0.12s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.lamp-knob {
  position: absolute;
  top: 0;
  left: 138px;
  z-index: 4;
  width: 24px;
  height: 23px;
  border-radius: 12px 12px 4px 4px;
  background: linear-gradient(90deg, #f58d39, #ffad5c 52%, #e97927);
}

.lamp-shade {
  position: absolute;
  top: 12px;
  left: 0;
  z-index: 5;
  width: 299px;
  height: 171px;
  border-radius: 50% 50% 9% 9% / 86% 86% 12% 12%;
  background:
    radial-gradient(circle at 68% 23%, rgba(255, 231, 190, 0.72), transparent 30%),
    linear-gradient(
      108deg,
      color(display-p3 0.949 0.545 0.247) 0%,
      color(display-p3 0.988 0.651 0.357) 33%,
      color(display-p3 1 0.773 0.498) 68%,
      color(display-p3 0.988 0.816 0.635) 100%
    );
  box-shadow:
    inset 0 -14px 22px rgba(209, 85, 25, 0.08),
    inset 0 -10px 18px rgba(255, 226, 157, 0.18),
    0 15px 28px rgba(217, 100, 28, 0.1),
    0 34px 58px rgba(255, 190, 80, 0.12);
  transform: perspective(540px) rotateX(-3deg);
}

.lamp-shade::after {
  content: "";
  position: absolute;
  right: 48px;
  bottom: -3px;
  left: 48px;
  height: 15px;
  border-radius: 50%;
  background: radial-gradient(
    ellipse,
    rgba(255, 253, 225, 0.9) 0%,
    rgba(255, 235, 164, 0.55) 35%,
    rgba(255, 199, 95, 0.14) 65%,
    transparent 79%
  );
  filter: blur(3.5px);
  box-shadow: 0 7px 18px rgba(255, 210, 125, 0.18);
  opacity: 0.86;
  animation: source-light-breathe 6.8s ease-in-out infinite;
}

.lamp-stem {
  position: absolute;
  top: 177px;
  left: 139px;
  z-index: 2;
  width: 22px;
  height: 305px;
  border-radius: 4px;
  background:
    linear-gradient(90deg, rgba(205, 84, 24, 0.12), transparent 46%, rgba(198, 72, 19, 0.17)),
    linear-gradient(
      180deg,
      color(display-p3 1 0.816 0.49) 0%,
      color(display-p3 0.992 0.784 0.451) 30%,
      color(display-p3 0.906 0.502 0.227) 78%,
      color(display-p3 0.906 0.502 0.227) 100%
    );
  box-shadow: 0 0 18px rgba(241, 124, 39, 0.2);
}

.lamp-cord {
  position: absolute;
  top: 178px;
  left: 199px;
  z-index: 3;
  width: 1px;
  height: 71px;
  background: #efbd61;
}

.lamp-pull {
  position: absolute;
  top: 246px;
  left: 191px;
  z-index: 4;
  width: 17px;
  height: 18px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffbc60, #ef8f31);
  box-shadow: 0 3px 8px rgba(210, 104, 29, 0.16);
}

.lamp-base {
  position: absolute;
  top: 474px;
  left: 67px;
  z-index: 3;
  width: 169px;
  height: 54px;
  border-radius: 50%;
  background: linear-gradient(180deg, #fa9844 0%, #e66b21 62%, #c95319 100%);
  box-shadow:
    inset 0 5px 7px rgba(255, 188, 104, 0.38),
    0 10px 15px rgba(167, 69, 22, 0.19);
}

.conversation {
  position: absolute;
  inset: 0;
  z-index: 8;
}

.chat-bubble {
  position: absolute;
  left: 374px;
  width: 205px;
  height: 54px;
  padding: 12px 12px 9px 18px;
  border: 1px solid rgba(235, 218, 195, 0.66);
  border-radius: 13px;
  box-sizing: border-box;
  background: color(display-p3 0.992 0.973 0.937 / 0.97);
  box-shadow: 0 7px 18px rgba(121, 71, 35, 0.11);
  color: var(--cocoa);
  animation: bubble-rise 0.66s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.chat-bubble::before {
  content: "";
  position: absolute;
  left: -8px;
  bottom: 5px;
  width: 17px;
  height: 17px;
  background: inherit;
  clip-path: polygon(0 52%, 100% 0, 100% 100%);
}

.chat-bubble:nth-child(1) {
  top: 229px;
  animation-delay: 0.34s;
}

.chat-bubble:nth-child(2) {
  top: 309px;
  left: 486px;
  width: 224px;
  padding-left: 18px;
  background: color(display-p3 0.992 0.871 0.741 / 0.98);
  animation-delay: 0.58s;
}

.chat-bubble:nth-child(2)::before {
  right: -9px;
  left: auto;
  transform: scaleX(-1);
}

.chat-bubble:nth-child(3) {
  top: 389px;
  animation-delay: 0.82s;
}

.chat-bubble p {
  margin: 0;
  font-size: 17px;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.bubble-time {
  position: absolute;
  right: 12px;
  bottom: 8px;
  color: #b5ab9d;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 10.5px;
  line-height: 1;
}

.outgoing .bubble-time {
  right: 10px;
}

.bubble-time i {
  margin-left: 4px;
  color: var(--orange);
  font-size: 12px;
  font-style: normal;
  font-weight: 800;
  letter-spacing: -0.18em;
}

.typing-bubble {
  position: absolute;
  top: 469px;
  left: 374px;
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 75px;
  height: 42px;
  border: 1px solid rgba(235, 218, 195, 0.66);
  border-radius: 13px;
  box-sizing: border-box;
  background: color(display-p3 0.992 0.973 0.937 / 0.98);
  box-shadow: 0 7px 18px rgba(121, 71, 35, 0.11);
  animation: bubble-rise 0.66s 1.05s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.typing-bubble::before {
  content: "";
  position: absolute;
  left: -8px;
  bottom: 5px;
  width: 17px;
  height: 17px;
  background: inherit;
  clip-path: polygon(0 52%, 100% 0, 100% 100%);
}

.typing-bubble i {
  position: relative;
  z-index: 1;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ff8651;
  animation: typing 1.35s ease-in-out infinite;
}

.typing-bubble i:nth-child(2) {
  animation-delay: 0.16s;
}

.typing-bubble i:nth-child(3) {
  animation-delay: 0.32s;
}

.table {
  position: absolute;
  top: 631px;
  left: -2px;
  z-index: 2;
  width: 912px;
  height: 310px;
}

.table-surface {
  position: absolute;
  top: 0;
  left: -20px;
  z-index: 3;
  width: 930px;
  height: 185px;
  border-radius: 0 108px 79px 0;
  background:
    radial-gradient(ellipse at 0 0, color(display-p3 1 0.56 0.19 / 0.22), transparent 53%),
    radial-gradient(ellipse at 0 100%, color(display-p3 0.65 0.42 0.28 / 0.16), transparent 50%),
    radial-gradient(ellipse at 66% 0, color(display-p3 0.945 0.78 0.62 / 0.5), transparent 37%),
    radial-gradient(ellipse at 91% 56%, color(display-p3 0.961 0.816 0.663 / 0.7), transparent 37%),
    linear-gradient(100deg, rgba(255, 255, 255, 0.12), transparent 37%),
    linear-gradient(
      180deg,
      color(display-p3 0.957 0.765 0.573) 0%,
      color(display-p3 0.937 0.745 0.557) 42%,
      color(display-p3 0.929 0.776 0.62) 100%
    );
  box-shadow:
    inset 0 12px 18px rgba(255, 218, 176, 0.2),
    0 14px 20px rgba(111, 54, 22, 0.1);
}

.table-surface::before {
  content: "";
  position: absolute;
  top: -12px;
  left: -10px;
  z-index: 0;
  width: 410px;
  height: 130px;
  border-radius: 50%;
  pointer-events: none;
  background: radial-gradient(
    ellipse at 50% 20%,
    rgba(255, 241, 200, 0.38) 0%,
    rgba(255, 220, 147, 0.18) 38%,
    rgba(255, 188, 96, 0.05) 62%,
    transparent 76%
  );
  filter: blur(10px);
  opacity: 0.72;
  mix-blend-mode: screen;
  animation: table-light-breathe 6.8s -1.2s ease-in-out infinite;
}

.table-surface::after {
  content: "";
  position: absolute;
  inset: 14px 42px auto 25px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(153, 86, 43, 0.1), transparent);
  box-shadow: 0 64px 0 rgba(153, 86, 43, 0.055), 0 124px 0 rgba(153, 86, 43, 0.055);
}

.table-front {
  position: absolute;
  top: 183px;
  left: -20px;
  z-index: 2;
  width: 828px;
  height: 77px;
  border-radius: 0 0 40px 0;
  background:
    linear-gradient(90deg, rgba(72, 34, 12, 0.12), transparent 90%),
    linear-gradient(
      180deg,
      color(display-p3 0.749 0.533 0.345) 0%,
      color(display-p3 0.749 0.533 0.345) 52%,
      color(display-p3 0.467 0.255 0.106) 55%,
      color(display-p3 0.6 0.4 0.239) 100%
    );
  box-shadow: inset 0 7px 12px rgba(92, 39, 16, 0.14);
}

.table-leg {
  position: absolute;
  top: 218px;
  left: 676px;
  z-index: 1;
  width: 137px;
  height: 176px;
  background: linear-gradient(90deg, #8d4b2b, #ac653d 65%, #7c3f24);
  clip-path: polygon(0 0, 100% 0, 82% 100%, 18% 100%);
  box-shadow: inset 12px 0 18px rgba(79, 34, 17, 0.14);
}

.still-life {
  position: absolute;
  top: 570px;
  left: 245px;
  z-index: 7;
  display: block;
  width: 500px;
  height: auto;
  pointer-events: none;
  user-select: none;
  filter: saturate(1.06) contrast(1.02) drop-shadow(0 10px 9px rgba(120, 60, 28, 0.15));
  animation: still-life-in 0.78s 0.32s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.steam {
  position: absolute;
  z-index: 9;
  overflow: visible;
  fill: none;
  stroke: rgba(224, 150, 89, 0.22);
  stroke-width: 5;
  stroke-linecap: round;
  filter: blur(0.4px);
  pointer-events: none;
}

.steam path {
  stroke-dasharray: 130;
  animation: steam-rise 3.4s ease-in-out infinite;
}

.steam path:nth-child(2) {
  animation-delay: 0.65s;
}

.steam-left {
  top: 518px;
  left: 294px;
  width: 53px;
  height: 72px;
}

.steam-right {
  top: 492px;
  left: 638px;
  width: 58px;
  height: 80px;
}

.form-panel {
  position: relative;
  z-index: 20;
  min-width: 0;
  height: 100vh;
  height: 100svh;
  overflow-y: auto;
}

.form-shell {
  position: relative;
  width: min(400px, calc(100% - 38px));
  min-height: 941px;
  padding-top: 178px;
  box-sizing: border-box;
  animation: form-enter 0.72s 0.18s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.panel-copy h1 {
  margin: 0 0 0 8px;
  color: var(--cocoa);
  font-size: 34px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: 0.055em;
}

.tabs {
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  width: 220px;
  height: 50px;
  margin-top: 18px;
  border-bottom: 2px solid var(--line);
}

.tabs button {
  position: relative;
  padding: 6px 0 0;
  border: 0;
  background: transparent;
  color: var(--cocoa);
  font: inherit;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
}

.tabs button::after {
  content: "";
  position: absolute;
  right: 16px;
  bottom: -3px;
  left: 0;
  height: 4px;
  border-radius: 5px;
  background: var(--orange);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.tabs button.on {
  color: var(--orange);
  font-weight: 650;
}

.tabs button.on::after {
  transform: scaleX(1);
}

.tabs button:first-child {
  padding-left: 28px;
  text-align: left;
}

.form-stage {
  margin-top: 32px;
}

.auth-form {
  width: 100%;
}

.auth-form ::v-deep .el-form-item {
  margin-bottom: 23px;
}

.auth-form ::v-deep .el-form-item__content {
  line-height: normal;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: var(--cocoa);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.25;
}

.field-shell {
  position: relative;
}

.auth-form ::v-deep .el-input__inner {
  height: 52px;
  padding: 0 14px;
  border: 1.5px solid #d8cbb8;
  border-radius: 10px;
  background: rgba(255, 252, 247, 0.78);
  color: var(--cocoa);
  font-size: 15.5px;
  box-shadow: 0 4px 14px rgba(121, 71, 35, 0.025);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.auth-form ::v-deep .el-input__inner::placeholder {
  color: #b2aea7;
}

.auth-form ::v-deep .el-input__inner:hover {
  border-color: rgba(253, 99, 40, 0.64);
}

.auth-form ::v-deep .el-input__inner:focus {
  border-color: var(--orange);
  background: #fffdf9;
  box-shadow: 0 0 0 4px rgba(253, 99, 40, 0.08), 0 8px 22px rgba(186, 84, 29, 0.07);
}

.login-form .field-item:nth-child(2) ::v-deep .el-input__inner {
  height: 50px;
}

.login-form .field-item:first-child ::v-deep .el-input__inner {
  border-color: var(--orange);
}

.has-toggle ::v-deep .el-input__inner {
  padding-right: 58px;
}

.field-toggle {
  position: absolute;
  top: 50%;
  right: 13px;
  z-index: 3;
  min-width: 34px;
  min-height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--orange);
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  transform: translateY(-50%);
}

.minor-row,
.panel-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.minor-row {
  margin: -1px 0 30px 2px;
}

.minor-row a,
.legal-copy a,
.check-control a,
.link-button {
  color: var(--orange);
  text-decoration: none;
}

.minor-row a:hover,
.legal-copy a:hover,
.check-control a:hover,
.link-button:hover {
  text-decoration: underline;
  text-underline-offset: 4px;
}

.minor-row a {
  font-size: 13.5px;
  font-weight: 500;
}

.check-control {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--body);
  font: inherit;
  font-size: 13.5px;
  line-height: 1.4;
  text-align: left;
  cursor: pointer;
}

.check-box {
  position: relative;
  display: inline-flex;
  width: 17px;
  height: 17px;
  border: 1.5px solid #d8cbb8;
  border-radius: 4px;
  box-sizing: border-box;
  background: #fffaf3;
  flex: 0 0 auto;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;
}

.check-box.checked {
  border-color: var(--orange);
  background: var(--orange);
}

.check-box.checked::after {
  content: "";
  position: absolute;
  top: 1px;
  left: 5px;
  width: 4px;
  height: 9px;
  border-right: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(45deg);
}

.submit-btn {
  display: block;
  width: 100%;
  height: 50px;
  padding: 0 20px;
  border: 0;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    color(display-p3 0.992 0.404 0.165) 0%,
    color(display-p3 0.992 0.404 0.165) 50%,
    color(display-p3 0.992 0.404 0.165) 100%
  );
  color: #fff;
  font-size: 17px;
  font-weight: 500;
  box-shadow: 0 12px 24px rgba(230, 76, 20, 0.12);
  transition: transform 0.18s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.submit-btn:hover,
.submit-btn:focus {
  border: 0;
  background: linear-gradient(
    90deg,
    color(display-p3 1 0.427 0.169) 0%,
    color(display-p3 0.957 0.325 0.094) 52%,
    color(display-p3 1 0.424 0.169) 100%
  );
  box-shadow: 0 16px 30px rgba(230, 76, 20, 0.18);
  filter: saturate(1.06);
  transform: translateY(-2px);
}

.submit-btn:active {
  transform: translateY(0);
}

.legal-copy,
.panel-footer,
.icp-copy {
  color: var(--body);
  font-size: 13px;
  text-align: center;
}

.legal-copy {
  margin: 16px 0 0;
  line-height: 1.55;
}

.icp-copy {
  position: absolute;
  right: 5px;
  bottom: 74px;
  left: -5px;
  margin: 0;
  color: #8f867d;
  font-size: 13px;
}

.icp-copy a {
  color: inherit;
  text-decoration: none;
}

.register-form ::v-deep .el-form-item {
  margin-bottom: 11px;
}

.register-form .field-label {
  margin-bottom: 5px;
  font-size: 13px;
}

.register-form ::v-deep .el-input__inner {
  height: 42px;
  border-radius: 9px;
  font-size: 14px;
}

.register-profile-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.register-profile-row .field-item {
  min-width: 0;
}

.code-row {
  display: flex;
  gap: 10px;
}

.code-field {
  flex: 1;
  min-width: 0;
}

.code-btn {
  width: 104px;
  height: 42px;
  border: 1.5px solid var(--orange);
  border-radius: 11px;
  background: rgba(255, 249, 240, 0.72);
  color: var(--orange);
  font-size: 12px;
  font-weight: 600;
}

.code-btn:hover,
.code-btn:focus {
  border-color: var(--orange-deep);
  background: rgba(253, 99, 40, 0.07);
  color: var(--orange-deep);
}

.agreement {
  align-items: flex-start;
  gap: 9px;
  margin: 6px 0 14px;
  font-size: 12px;
  line-height: 1.55;
}

.agreement .check-box {
  width: 18px;
  height: 18px;
  margin-top: 1px;
}

.agreement .check-box.checked::after {
  top: 2px;
  left: 5px;
  width: 4px;
  height: 8px;
}

.register-form .submit-btn {
  height: 46px;
  border-radius: 10px;
  font-size: 15.5px;
}

.panel-footer {
  justify-content: center;
  gap: 7px;
  margin-top: 14px;
  font-size: 12px;
}

.link-button {
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.is-register .form-shell {
  padding-top: 74px;
}

.is-register .panel-copy h1 {
  font-size: 32px;
}

.is-register .tabs {
  margin-top: 12px;
}

.is-register .form-stage {
  margin-top: 18px;
}

.is-register .icp-copy {
  bottom: 22px;
}

.bubble-change-enter-active {
  transition: opacity 0.34s ease, transform 0.34s ease;
}

.bubble-change-enter {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

.bubble-change-leave-active {
  transition: opacity 0.12s ease;
}

.bubble-change-leave-to {
  opacity: 0;
}

.heading-change-enter-active,
.heading-change-leave-active,
.form-change-enter-active,
.form-change-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.heading-change-enter,
.form-change-enter {
  opacity: 0;
  transform: translateY(8px);
}

.heading-change-leave-to,
.form-change-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@keyframes reveal-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes brand-reveal {
  from {
    opacity: 0;
    transform: translateY(10px) scaleX(1.19);
  }
  to {
    opacity: 1;
    transform: translateY(0) scaleX(1.19);
  }
}

@keyframes lamp-settle {
  from {
    opacity: 0;
    transform: translateY(-12px) rotate(-1.2deg);
  }
  to {
    opacity: 1;
    transform: translateY(0) rotate(0);
  }
}

@keyframes glow-breathe {
  0%,
  100% {
    opacity: 0.2;
    transform: scale(0.995);
  }
  50% {
    opacity: 0.25;
    transform: scale(1.006);
  }
}

@keyframes ambient-light-breathe {
  0%,
  100% {
    opacity: 0.69;
    transform: scale(0.995);
  }
  50% {
    opacity: 0.75;
    transform: scale(1.008);
  }
}

@keyframes source-light-breathe {
  0%,
  100% {
    opacity: 0.84;
  }
  50% {
    opacity: 0.9;
  }
}

@keyframes table-light-breathe {
  0%,
  100% {
    opacity: 0.69;
    transform: scaleX(0.995);
  }
  50% {
    opacity: 0.74;
    transform: scaleX(1.008);
  }
}

@keyframes bubble-rise {
  from {
    opacity: 0;
    transform: translateY(14px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes typing {
  0%,
  60%,
  100% {
    opacity: 0.55;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-4px);
  }
}

@keyframes still-life-in {
  from {
    opacity: 0;
    transform: translateY(15px) scale(0.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes steam-rise {
  0% {
    opacity: 0;
    stroke-dashoffset: 36;
    transform: translateY(8px);
  }
  26% {
    opacity: 0.72;
  }
  72% {
    opacity: 0.42;
  }
  100% {
    opacity: 0;
    stroke-dashoffset: -60;
    transform: translateY(-7px);
  }
}

@keyframes form-enter {
  from {
    opacity: 0;
    transform: translateX(18px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-height: 880px) and (min-width: 921px) {
  .scene-panel {
    min-height: 820px;
  }

  .form-shell {
    min-height: 820px;
    padding-top: 92px;
  }

  .icp-copy {
    bottom: 30px;
  }

  .conversation,
  .typing-bubble,
  .table,
  .still-life,
  .steam {
    transform: translateY(-55px);
  }

  .lamp-wrap {
    transform: translateY(-55px) scale(0.79);
  }
}

@media (max-width: 1180px) and (min-width: 921px) {
  .auth-view {
    grid-template-columns: 56% 44%;
  }

  .scene-panel::after {
    background: linear-gradient(90deg, transparent 78%, rgba(254, 243, 228, 0.76) 96%, var(--cream) 100%);
  }

  .conversation {
    transform: scale(0.86);
    transform-origin: 42% 30%;
  }

  .still-life {
    left: 16%;
    width: 62%;
  }

  .form-shell {
    width: min(400px, calc(100% - 32px));
  }
}

@media (max-width: 920px) {
  .auth-view {
    display: block;
    min-width: 0;
    min-height: 100svh;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .scene-panel {
    width: 100%;
    min-height: 300px;
    height: 300px;
  }

  .scene-panel::after {
    background: linear-gradient(180deg, transparent 80%, var(--cream) 100%);
  }

  .brand-word {
    top: 25px;
    left: 25px;
    font-size: 24px;
  }

  .lamp-wrap {
    top: 58px;
    left: -4px;
    transform: scale(0.36);
    transform-origin: top left;
  }

  .conversation {
    top: -126px;
    left: 4%;
    transform: scale(0.48);
    transform-origin: center;
  }

  .chat-bubble:nth-child(3),
  .typing-bubble,
  .steam {
    display: none;
  }

  .table {
    top: 218px;
    left: -4%;
    width: 108%;
    height: 96px;
  }

  .table-surface {
    width: 100%;
    height: 82px;
    border-radius: 0 54px 0 0;
  }

  .table-front {
    top: 70px;
    width: 100%;
    height: 30px;
  }

  .table-leg {
    display: none;
  }

  .still-life {
    top: 168px;
    right: -20px;
    left: auto;
    width: min(58vw, 370px);
  }

  .form-panel {
    height: auto;
    min-height: 580px;
    overflow: visible;
  }

  .form-shell,
  .is-register .form-shell {
    width: min(400px, calc(100% - 54px));
    min-height: 580px;
    margin: 0 auto;
    padding-top: 22px;
    padding-bottom: 94px;
  }

  .panel-copy h1,
  .is-register .panel-copy h1 {
    font-size: 28px;
  }

  .tabs,
  .is-register .tabs {
    width: 100%;
    height: 48px;
    margin-top: 11px;
  }

  .form-stage,
  .is-register .form-stage {
    margin-top: 26px;
  }

  .icp-copy,
  .is-register .icp-copy {
    bottom: 28px;
  }
}

@media (max-width: 560px) {
  .scene-panel {
    height: 240px;
    min-height: 240px;
  }

  .conversation {
    top: -146px;
    left: -10%;
    transform: scale(0.4);
  }

  .lamp-wrap {
    top: 54px;
    transform: scale(0.3);
  }

  .table {
    top: 183px;
  }

  .still-life {
    top: 139px;
    width: 60vw;
  }

  .form-shell,
  .is-register .form-shell {
    width: calc(100% - 54px);
  }

  .panel-copy h1,
  .is-register .panel-copy h1 {
    font-size: 24px;
  }

  .auth-form ::v-deep .el-input__inner {
    height: 50px;
    font-size: 15px;
  }

  .field-label {
    font-size: 14.5px;
  }

  .minor-row a,
  .check-control,
  .legal-copy {
    font-size: 13px;
  }

  .submit-btn {
    height: 50px;
    font-size: 16px;
  }

  .code-btn {
    width: 96px;
  }

  .legal-copy {
    line-height: 1.8;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
